import React, { useState, useEffect, useCallback } from 'react';
import { apiJson, apiPost, apiPut, apiDelete } from '../api';
import { colors, theme } from '../styles';

const TONES = ['friendly', 'professional', 'casual', 'enthusiastic'];

export default function Comments({ account }) {
  const [templates, setTemplates] = useState([]);
  const [targets, setTargets] = useState([]);
  const [form, setForm] = useState({ template: '', tone: 'friendly', target_id: '' });
  const [previews, setPreviews] = useState([]);

  const load = useCallback(async () => {
    const [tplData, tgtData] = await Promise.all([
      apiJson(`/accounts/${account.id}/comments`),
      apiJson(`/accounts/${account.id}/targets`),
    ]);
    if (tplData) setTemplates(tplData.templates);
    if (tgtData) setTargets(tgtData.targets);
  }, [account.id]);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.template.trim()) return;
    const body = { template: form.template, tone: form.tone };
    if (form.target_id) body.target_id = parseInt(form.target_id);
    await apiPost(`/accounts/${account.id}/comments`, body);
    setForm({ template: '', tone: 'friendly', target_id: '' });
    setPreviews([]);
    load();
  };

  const preview = async () => {
    if (!form.template.trim()) return;
    const data = await apiPost('/comments/preview', { template: form.template, count: 5 });
    if (data) setPreviews(data.previews);
  };

  const toggleEnabled = async (t) => {
    await apiPut(`/comments/${t.id}`, { enabled: t.enabled ? 0 : 1 });
    load();
  };

  const remove = async (id) => {
    await apiDelete(`/comments/${id}`);
    load();
  };

  const getTargetLabel = (targetId) => {
    if (!targetId) return null;
    const t = targets.find(x => x.id === targetId);
    return t ? t.target_value : `#${targetId}`;
  };

  return (
    <div>
      <h2 style={{ color: '#fff', fontSize: 20, margin: '0 0 16px' }}>Comment Templates: @{account.username}</h2>

      {/* How-to */}
      <div style={{ ...theme.card, marginBottom: 16, borderColor: colors.accent + '44' }}>
        <h3 style={theme.sectionTitle}>Spin Syntax Guide</h3>
        <div style={{ fontSize: 12, color: colors.textMuted, lineHeight: 1.8 }}>
          Use <code style={codeStyle}>{'{option1|option2|option3}'}</code> to randomly pick one.<br />
          <strong style={{ color: colors.text }}>Example:</strong>{' '}
          <code style={codeStyle}>{'{Great|Awesome} {post|content}! {Love it|So good}!'}</code>
        </div>
      </div>

      {/* Add template */}
      <div style={{ ...theme.card, marginBottom: 16 }}>
        <h3 style={theme.sectionTitle}>Add Template</h3>
        <div style={theme.row}>
          <div style={{ width: 140 }}>
            <label style={theme.label}>Tone</label>
            <select style={theme.select} value={form.tone} onChange={e => setForm({ ...form, tone: e.target.value })}>
              {TONES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <label style={theme.label}>Template (with spin syntax)</label>
            <input style={theme.input} value={form.template}
              onChange={e => setForm({ ...form, template: e.target.value })}
              placeholder="{Great|Awesome} {post|content}! {Love this|So good}!" />
          </div>
        </div>
        <div style={{ marginTop: 8 }}>
          <label style={theme.label}>Target Association (optional)</label>
          <select style={{ ...theme.select, maxWidth: 300 }} value={form.target_id}
            onChange={e => setForm({ ...form, target_id: e.target.value })}>
            <option value="">Global (used for all targets)</option>
            {targets.map(t => (
              <option key={t.id} value={t.id}>{t.target_type === 'keyword' ? t.target_value : `@${t.target_value}`}</option>
            ))}
          </select>
          <div style={{ fontSize: 11, color: colors.textDimmer, marginTop: 4 }}>
            If set, this template only fires when processing the selected target. Global templates are used for all targets.
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button style={theme.btnPrimary} onClick={preview}>Preview</button>
          <button style={theme.btnSuccess} onClick={add}>Add Template</button>
        </div>

        {previews.length > 0 && (
          <div style={{ marginTop: 12, padding: 12, background: colors.bgDark, borderRadius: 6 }}>
            <div style={{ fontSize: 11, color: colors.textDim, marginBottom: 6 }}>PREVIEW:</div>
            {previews.map((p, i) => (
              <div key={i} style={{ fontSize: 13, color: colors.green, padding: '3px 0' }}>"{p}"</div>
            ))}
          </div>
        )}
      </div>

      {/* Template list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {templates.map(t => (
          <div key={t.id} style={{
            ...theme.card, padding: '10px 16px',
            display: 'flex', alignItems: 'center', gap: 12,
            opacity: t.enabled ? 1 : 0.4,
          }}>
            <span style={theme.badge(
              t.tone === 'friendly' ? colors.green : t.tone === 'professional' ? colors.blue :
              t.tone === 'casual' ? colors.orange : colors.red
            )}>{t.tone}</span>
            {t.target_label ? (
              <span style={theme.badge(colors.accent)}>{t.target_label}</span>
            ) : (
              <span style={theme.badge(colors.textDim)}>global</span>
            )}
            <span style={{ flex: 1, color: colors.text, fontSize: 13, fontFamily: 'monospace' }}>{t.template}</span>
            <button style={{ ...theme.btnSmall, background: t.enabled ? colors.orange : colors.green, color: '#000', fontWeight: 600 }}
              onClick={() => toggleEnabled(t)}>{t.enabled ? 'Disable' : 'Enable'}</button>
            <button style={{ ...theme.btnSmall, background: colors.redDark, color: '#fff' }}
              onClick={() => remove(t.id)}>X</button>
          </div>
        ))}
        {templates.length === 0 && (
          <div style={{ color: colors.textDim, fontSize: 13, textAlign: 'center', padding: 20 }}>
            No custom templates. Built-in defaults will be used.
          </div>
        )}
      </div>
    </div>
  );
}

const codeStyle = { background: colors.bgInput, padding: '2px 6px', borderRadius: 3, fontSize: 12, color: colors.accent };
